<template>
  <div class="flex flex-col gap-3">
    <div v-if="loading" class="flex items-center justify-center">
      <InlineLoader />
    </div>
    <div v-for="(level, index) in levels" :key="index" class="flex flex-col gap-1">
      <label class="font-semibold">{{ level.label }}<span v-if="required && index === 0" class="text-red-600">*</span></label>
      <Multiselect 
        v-model="level.inputValue" 
        @select="handleSelect($event, index)" 
        :options="level.transformedOptions"
        :searchable="true"
        :close-on-select="true">
      </Multiselect>
      <p class="text-red-500" v-show="level.errorMessage">{{ level.errorMessage }}</p>
    </div>
  </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n';
import { useField } from 'vee-validate';
import * as yup from 'yup';
import { ref, reactive, computed, watch } from 'vue';
import Multiselect from '@vueform/multiselect';
import InlineLoader from '@cmp/InlineLoader.vue';

const { t } = useI18n();

const props = defineProps({
  label: {
    type: String,
    required: true
  },
  name: {
    type: String,
    required: true
  },
  required: {
    type: Boolean,
    default: true
  },
  options: {
    type: Array,
    required: true
  },
  property: {
    type: Number,
    required: true
  },
  defaultValues: {
    type: Array,
    default: () => []
  }
});

const levels = ref([]);
const loading = ref(false)
const initialized = ref(false);

const rules = yup.number().required(t('warning.REQUIRED_MESSAGE'));

const createLevel = (name, options, label, initialValue = null) => {
  const { meta, value, errorMessage, validate, handleChange } = useField(name, rules, {
    initialValue,
  });

  return reactive({
    name,
    label,
    meta,
    inputValue: value,
    errorMessage,
    validate,
    handleChange,
    options,
    transformedOptions: computed(() => options.map(item => ({
      value: item.id,
      label: item.name,
      children: item.children,
      property: item.property
    }))),
  });
};

const buildDefaultChain = () => {
  const chain = [];
  let currentProperty = props.property;
  let currentDefaultValues = [...props.defaultValues];

  // Find the root level default value
  let currentDefault = currentDefaultValues.find(item => item.property === currentProperty);
  
  while (currentDefault) {
    chain.push(currentDefault);
    
    // Check if this item has children in defaults
    const childProperty = currentDefault.children_property?.id;
    if (!childProperty) break;
    
    currentProperty = childProperty;
    currentDefault = currentDefaultValues.find(item => item.property === currentProperty && 
      item.parent?.id === chain[chain.length - 1].id);
  }

  return chain;
};

const initializeLevels = () => {
  levels.value = [];
  const defaultChain = buildDefaultChain();

  // Create first level with root options
  const rootOptions = props.options.filter(option => option.property === props.property);
  const firstLevelDefault = defaultChain[0]?.id || null;
  
  if (rootOptions.length === 0) return;

  const firstLevel = createLevel(
    `${props.name}-0`,
    rootOptions,
    props.label,
    firstLevelDefault
  );
  levels.value.push(firstLevel);

  // Create subsequent levels based on default chain
  for (let i = 1; i < defaultChain.length; i++) {
    const parentLevel = levels.value[i - 1];
    const parentOption = parentLevel.options.find(opt => opt.id === parentLevel.inputValue);
    
    if (parentOption?.children) {
      const level = createLevel(
        `${props.name}-${i}`,
        parentOption.children,
        parentOption.children_property?.name || `Level ${i}`,
        defaultChain[i]?.id || null
      );
      levels.value.push(level);
    }
  }

  initialized.value = true;
};

const handleSelect = (selectedId, levelIndex) => {
  // Remove all levels after the current one
  levels.value = levels.value.slice(0, levelIndex + 1);
  
  const selectedOption = levels.value[levelIndex].options.find(opt => opt.id === selectedId);
  
  if (selectedOption?.children?.length) {
    const nextLevel = createLevel(
      `${props.name}-${levels.value.length}`,
      selectedOption.children,
      selectedOption.children_property?.name || `Level ${levels.value.length}`,
      null
    );
    levels.value.push(nextLevel);
  }
};

const validateField = async () => {
  if (!initialized.value) {
    return { value: [], valid: false, mode: 'multi', property: props.property };
  }

  const results = await Promise.all(levels.value.map(level => level.validate()));
  const valid = results.every(result => result.valid);
  const values = levels.value.map(level => level.inputValue);
  
  return { 
    value: values, 
    valid, 
    mode: 'multi', 
    property: props.property 
  };
};

// Watchers
watch(() => props.options, (newOptions) => {
  if (props.options == undefined){
    loading.value = true
  }else{
    loading.value = false
  }
  if (newOptions && newOptions.length > 0) {
    initializeLevels();
  }
}, { immediate: true });

watch(() => props.defaultValues, () => {
  if (props.options && props.options.length > 0) {
    initializeLevels();
  }
});

defineExpose({ validateField });
</script>

<style>
@import '@vueform/multiselect/themes/tailwind.css';
</style>